import React, { useEffect, useState } from "react";

import axios from "axios";
import classNames from "classnames/bind";
import UIIcon, { UIIconProps } from "common/blocks/UIIcon";
import { AnimatePresence, motion } from "framer-motion";

import styles from "./Accordion.module.scss";

const cx = classNames.bind(styles);

export interface AccordionProps {
    title: string;
    subTitle?: string;
    children: React.ReactNode;
    summary?: React.ReactNode;
    defaultOpen?: boolean;
    classNames?: {
        wrapper?: string;
        heading?: string;
    };
    OpenIcon?: UIIconProps["icon"];
    ClosedIcon?: UIIconProps["icon"];
}

const Accordion = (props: AccordionProps) => {
    const {
        title,
        subTitle,
        children,
        summary,
        defaultOpen = false,
        classNames,
        OpenIcon,
        ClosedIcon,
    } = props;

    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

    return (
        <div className={cx("block", classNames?.wrapper, { isOpen })}>
            <motion.button
                initial={false}
                onClick={() => setIsOpen(!isOpen)}
                className={cx("header", { isOpen }, classNames?.heading)}
                aria-controls={title}
                aria-expanded={isOpen}
                type="button"
            >
                <div className={cx("titleWrap")}>
                    <span className={cx("title")}>{title}</span>
                </div>
                {subTitle && <span className={cx("subTitle")}>{subTitle}</span>}
                {isOpen && (
                    <UIIcon icon={OpenIcon ? OpenIcon : "ChevronUp"} className={cx("icon")} />
                )}
                {!isOpen && (
                    <UIIcon icon={ClosedIcon ? ClosedIcon : "ChevronDown"} className={cx("icon")} />
                )}
                {summary && (
                    <AnimatePresence initial={true}>
                        {!isOpen && (
                            <motion.div
                                key="summary"
                                initial={defaultOpen ? "open" : "collapsed"}
                                animate="open"
                                exit="collapsed"
                                variants={{
                                    open: { height: "auto" },
                                    collapsed: { height: 0 },
                                }}
                                transition={{
                                    duration: 0.3,
                                    ease: [0.04, 0.62, 0.23, 0.98],
                                }}
                                aria-hidden={isOpen}
                                className={cx("summary")}
                            >
                                <motion.div
                                    variants={{
                                        collapsed: {
                                            opacity: 0,
                                            transition: {
                                                duration: 0.3,
                                                delay: 0,
                                                ease: [0.04, 0.62, 0.23, 0.98],
                                            },
                                        },
                                        open: {
                                            opacity: 1,
                                            transition: {
                                                duration: 0.3,
                                                delay: 0.2,
                                            },
                                        },
                                    }}
                                    className={cx("summaryWrap")}
                                >
                                    {summary}
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                )}
            </motion.button>

            <AnimatePresence initial={true}>
                {isOpen && (
                    <motion.section
                        key="children"
                        initial={defaultOpen ? "open" : "collapsed"}
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { height: "auto" },
                            collapsed: { height: 0 },
                        }}
                        transition={{
                            duration: 0.3,
                            ease: [0.04, 0.62, 0.23, 0.98],
                        }}
                        className={cx("content")}
                        aria-hidden={!isOpen}
                    >
                        <motion.div
                            variants={{
                                collapsed: {
                                    opacity: 0,
                                    y: [0, 10],
                                    transition: {
                                        duration: 0.3,
                                        delay: 0,
                                        ease: [0.04, 0.62, 0.23, 0.98],
                                    },
                                },
                                open: {
                                    opacity: 1,
                                    transition: {
                                        duration: 0.3,
                                        delay: 0.2,
                                    },
                                },
                            }}
                            aria-hidden={!isOpen}
                        >
                            {children}
                        </motion.div>
                    </motion.section>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Accordion;
